// Capacitor native-shell setup.
// Safe to import in web builds — all calls no-op outside the native app.
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapApp } from '@capacitor/app';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { pushAPI } from './api';

const isNative = Capacitor.isNativePlatform();

export const isMobileApp = isNative;

// Request permission, get the FCM device token, and register it with the backend.
export const registerPushNotifications = async () => {
  if (!isNative) return;
  try {
    const perm = await FirebaseMessaging.requestPermissions();
    if (perm.receive !== 'granted') return;
    const { token } = await FirebaseMessaging.getToken();
    if (token) {
      try { await pushAPI.register(token, Capacitor.getPlatform()); } catch (e) { /* backend may be old */ }
    }
    await FirebaseMessaging.addListener('tokenReceived', async (event) => {
      if (event?.token) {
        try { await pushAPI.register(event.token, Capacitor.getPlatform()); } catch (e) { /* noop */ }
      }
    });
  } catch (e) {
    console.warn('Push registration failed', e);
  }
};

export const initCapacitor = async (navigate) => {
  if (!isNative) return;

  try {
    // App header is white/light, so use dark status-bar icons for contrast.
    await StatusBar.setOverlaysWebView({ overlay: true });
    await StatusBar.setStyle({ style: Style.Light });
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    }
  } catch (e) {
    // Status bar plugin can fail silently on unusual devices
    console.warn('StatusBar setup failed', e);
  }

  // Hide splash once React has mounted
  try {
    await SplashScreen.hide();
  } catch (e) {
    /* noop */
  }

  // Deep-link handling — taps on https://crew.amiphotovideo.com/<path> from outside the app
  // route us correctly inside the app (Universal Links / App Links).
  if (navigate) {
    CapApp.addListener('appUrlOpen', (event) => {
      try {
        const url = new URL(event.url);
        const path = url.pathname + url.search;
        if (path) navigate(path);
      } catch (e) {
        console.warn('Failed to parse appUrlOpen', event.url, e);
      }
    });
  }

  // Optional: warn before navigating away (Android back button at root)
  CapApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      CapApp.exitApp();
    }
  });
};
